import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { ReactComponent as LockIcon } from 'src/icons/eye-hidden.svg';
import { ReactComponent as PersonIcon } from 'src/icons/user.svg';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import brandLogo from 'src/assets/icons/logo.png';
import { useAppDispatch } from 'src/hooks';
import { login } from 'src/reducers/user';
import { Input, Text } from 'src/shared';
import { Size } from 'src/shared/text';
import { getAuthToken, setAuthTokens } from 'src/utils/auth';
import { Colors } from 'src/utils/color';

import {
    LoginContainer,
    TopNavBar,
    NavLeft,
    NavLogo,
    NavLinks,
    NavButton,
    MainContent,
    LeftSection,
    RightSection,
    BrandContent,
    BrandLogo,
    BrandImage,
    BrandText,
    LoginFormContainer,
    WelcomeTitle,
    WelcomeSubtitle,
    FormWrapper,
    FormField,
    RememberMeWrapper,
    RememberMe,
    SignInButton,
    SignUpLink,
    ForgotPassword,
    StyledInput,
    StyledLabel,
    StyledSwitch
} from './styles';

const Login = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [rememberMe, setRememberMe] = useState(false);
    
    const validationSchema = yup.object().shape({
        email: yup
            .string()
            .required('Email is required')
            .email('Invalid email'),
        password: yup.string().required('Password is required')
    });
    const [errorMessage, setErrorMessage] = useState<string>('');

    const onSubmit = async (data: any) => {
        dispatch(
            login({
                email: data.email,
                password: data.password
            })
        )
            .unwrap()
            .then((response: any) => {
                if (response?.success) {
                    setAuthTokens(response?.data?.access, response?.data?.refresh);
                    navigate('/retina');
                } else {
                    setErrorMessage(response?.message);
                }
            })
            .catch((err: any) => {
                console.error('Login failed', err);
                setErrorMessage('Something went wrong. Please try again.');
            });
    };
    
    if (getAuthToken()) return <Navigate to="/retina" />;
    
    return (
        <LoginContainer>
            <TopNavBar>
                <NavLeft>
                    <NavLogo>RetiOne</NavLogo>
                    <NavLinks>
                        <Link to="/retina">Dashboard</Link>
                        <Link to="/patients">Patients</Link>
                        <Link to="/scans">Scans</Link>
                        <Link to="/analytics">Analytics</Link>
                    </NavLinks>
                </NavLeft>
                <NavButton onClick={() => navigate('/retina')}>
                    Get Started
                </NavButton>
            </TopNavBar>
            
            <MainContent>
                <LeftSection>
                    <LoginFormContainer>
                        <WelcomeTitle>Welcome Back</WelcomeTitle>
                        <WelcomeSubtitle>
                            Enter your email and password to sign in
                        </WelcomeSubtitle>
                        
                        <FormWrapper>
                            <Formik
                                initialValues={{
                                    email: '',
                                    password: ''
                                }}
                                validationSchema={validationSchema}
                                onSubmit={onSubmit}
                                validateOnMount={false}
                            >
                                {({
                                    values,
                                    handleChange,
                                    handleSubmit,
                                    errors,
                                    touched
                                }: {
                                    values: { email: string; password: string };
                                    handleChange: (field: string) => (e: React.ChangeEvent<any>) => void;
                                    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
                                    errors: { email?: string; password?: string };
                                    touched: { email?: boolean; password?: boolean };
                                }) => (
                                    <Form onSubmit={handleSubmit}>
                                        {errorMessage && (
                                            <Alert variant="danger" style={{ marginBottom: '24px' }}>
                                                {errorMessage}
                                            </Alert>
                                        )}
                                        
                                        <FormField>
                                            <StyledLabel>Email</StyledLabel>
                                            <StyledInput
                                                type="text"
                                                name="email"
                                                placeholder="Your email address"
                                                value={values.email}
                                                onChange={handleChange('email')}
                                            />
                                            {touched.email && errors.email && (
                                                <Text
                                                    marginTop={4}
                                                    marginBottom={0}
                                                    color={Colors.extra.red}
                                                    fontSize={Size.XXXSmall}
                                                >
                                                    {errors.email}
                                                </Text>
                                            )}
                                        </FormField>
                                        
                                        <FormField>
                                            <StyledLabel>Password</StyledLabel>
                                            <StyledInput
                                                type="password"
                                                name="password"
                                                placeholder="Your password"
                                                value={values.password}
                                                onChange={handleChange('password')}
                                            />
                                            {touched.password && errors.password && (
                                                <Text
                                                    marginTop={4}
                                                    marginBottom={0}
                                                    color={Colors.extra.red}
                                                    fontSize={Size.XXXSmall}
                                                >
                                                    {errors.password}
                                                </Text>
                                            )}
                                        </FormField>
                                        
                                        <RememberMeWrapper>
                                            <RememberMe>
                                                <StyledSwitch
                                                    type="checkbox"
                                                    id="remember-me"
                                                    checked={rememberMe}
                                                    onChange={(e) => setRememberMe(e.target.checked)}
                                                />
                                                <label htmlFor="remember-me">Remember me</label>
                                            </RememberMe>
                                        </RememberMeWrapper>
                                        
                                        <SignInButton type="submit">
                                            SIGN IN
                                        </SignInButton>
                                        
                                        <SignUpLink>
                                            Don't have an account?{' '}
                                            <Link to="/forgot-password">Sign up</Link>
                                        </SignUpLink>
                                        
                                        <ForgotPassword>
                                            <Link to="/forgot-password">
                                                Forgot Password?
                                            </Link>
                                        </ForgotPassword>
                                    </Form>
                                )}
                            </Formik>
                        </FormWrapper>
                    </LoginFormContainer>
                </LeftSection>
                
                <RightSection>
                    <BrandContent>
                        <BrandLogo>
                            <BrandImage src={brandLogo} alt="C. Light Technologies Logo" />
                        </BrandLogo>
                        <BrandText>RetiOne</BrandText>
                        <BrandText style={{ fontSize: '18px', marginTop: '16px', opacity: 0.9 }}>
                            AI-Powered Retinal Analysis
                        </BrandText>
                    </BrandContent>
                </RightSection>
            </MainContent>
        </LoginContainer>
    );
};

export default Login;
